/**
 * Single-flight lock for the legacy -> React "bo-login" handshake.
 *
 * The legacy backoffice hands React a one-shot token (Spring deletes it after the first successful
 * bo-login). Any second call carrying the same token fails with a 404, which used to trigger another
 * legacy redirect and therefore a redirect loop. This module guarantees that:
 *  - only one bo-login runs at a time (a second caller simply gets the pending promise),
 *  - other parts of the app (axios interceptor, heartbeat…) can know a login is in progress and must
 *    NOT redirect to the legacy backoffice meanwhile: the login result decides what happens next.
 *
 * Deliberately dependency-free so it can be imported anywhere without creating import cycles.
 */
let pending: Promise<unknown> | null = null;

export const BoLoginFlow = {
    isInProgress(): boolean {
        return pending !== null;
    },

    /** The login currently running, if any (lets a remounted component wait for it instead of starting another). */
    getPending(): Promise<unknown> | null {
        return pending;
    },

    run<T>(login: () => Promise<T>): Promise<T> {
        if (pending) {
            return pending as Promise<T>;
        }

        const current: Promise<T> = login().finally(() => {
            if (pending === current) {
                pending = null;
            }
        });
        pending = current;
        return current;
    },
};
